import { SettingsIcon } from '@chakra-ui/icons'
import {
  Box,
  Button,
  Center,
  Flex,
  Icon,
  Menu,
  MenuButton,
  MenuItem,
  MenuList,
  Table,
  TableCaption,
  Tbody,
  Td,
  Text,
  Th,
  Thead,
  Tooltip,
  Tr,
  VStack,
} from '@chakra-ui/react'
import styled from '@emotion/styled'
import { Prescription } from '../../../types'
import { printPaperPrescription } from '../../NewScriptPage/SendScriptPanel/PaperPrescription'
import { transformPrescriptions } from '../utilities'
import { printEvidenceOfPrescription } from './EvidenceOfPrescription'
import PrescribedItemStateTag from './PrescribedItemStateTag'
import { PrescriptionTypeTag } from './PrescriptionTypeTag'
import { TruncatedText } from '../../../components/shared-components/TruncatedText'
import { FiFileText } from 'react-icons/fi'
import { MdLocationOn } from 'react-icons/md'

const StyledWrapper = styled(Box)`
  border: 1px solid #555555;
  border-radius: 16px;
  max-height: 100%;
  background-color: white;
  overflow: hidden;
`

const StyledTable = styled(Table)`
  th,
  td {
    padding: 16px !important;
  }
`

interface PrescriptionTableProps {
  prescriptions: Prescription[]
  isDeactivatedPrescriber?: boolean
  isReadonlyPermission?: boolean
  openCeaseModal: (index: number) => void
  openReIssueModal: (index: number) => void
  openCancelModal: (index: number) => void
  isSharedPatientsFeatureOn: boolean
  isMultipleLocationsFeatureOn: boolean
  currentUserPrescriberId?: string | null
}

const PrescriptionTable: React.FC<PrescriptionTableProps> = ({
  prescriptions,
  openCeaseModal,
  openReIssueModal,
  openCancelModal,
  isDeactivatedPrescriber,
  isReadonlyPermission,
  isSharedPatientsFeatureOn,
  isMultipleLocationsFeatureOn,
  currentUserPrescriberId,
}) => {
  const viewPaperPrescription = (prescription: Prescription) => {
    printPaperPrescription(prescription)
  }

  const viewElectronicPrescription = (scid: string) => {
    window.open(`${process.env.REACT_APP_ELECTRONIC_PRESCRIPTION_BASE_URL}/${scid}`, '_blank')
  }

  return (
    <StyledWrapper>
      <Box overflowX="auto" overflowY="auto" height="100%" width="100%">
        {prescriptions.length > 0 ? (
          <StyledTable variant="striped" size={['md', 'lg']} sx={{ tableLayout: 'fixed' }}>
            <TableCaption />
            <Thead position="sticky" top={0} backgroundColor="white" zIndex={1}>
              <Tr>
                <Th width="120px">Actions</Th>
                <Th width="150px">Patient Name</Th>
                {isSharedPatientsFeatureOn && <Th width="150px">Prescriber Name</Th>}
                {isMultipleLocationsFeatureOn && (
                  <Th width="140px" color="gray.600" fontWeight="semibold">
                    LOCATION
                  </Th>
                )}
                <Th width="120px">SCID</Th>
                <Th width="100px">Type</Th>
                <Th width="130px">Created Date</Th>
                <Th width="220px">Prescribed Item Name | Quantity</Th>
                <Th width="120px">Original Repeats</Th>
                <Th width="200px">Patient Instructions</Th>
              </Tr>
            </Thead>
            <Tbody>
              {prescriptions.map((prescription, index) => {
                const transformed = transformPrescriptions(prescription)
                const {
                  patientName,
                  prescriberName,
                  createdDate,
                  prescribedItems,
                  isPaperPrescription,
                  scid,
                  isEmergencySupply,
                  isSendToPharmacy,
                } = transformed
                const isItemInactive =
                  prescribedItems[0].state === 'Ceased' || prescribedItems[0].state === 'Cancelled'
                const isSelfPrescribed =
                  prescription.issueSnapshot.prescriber.id === currentUserPrescriberId
                return (
                  <Tr key={`${prescription.id}`}>
                    <Td textAlign={'center'}>
                      <Menu>
                        <MenuButton as={Button} colorScheme="blue" variant="link" width={'100%'}>
                          <SettingsIcon />
                        </MenuButton>
                        <MenuList>
                          <Tooltip
                            label="View the electronic prescription for your patient."
                            placement="right"
                          >
                            <MenuItem
                              isDisabled={isItemInactive}
                              onClick={() => {
                                isPaperPrescription
                                  ? viewPaperPrescription(prescription)
                                  : viewElectronicPrescription(scid)
                              }}
                            >
                              View
                            </MenuItem>
                          </Tooltip>
                          <Tooltip
                            label={'Print evidence of electronic prescription.'}
                            placement={'right'}
                          >
                            <MenuItem
                              isDisabled={isPaperPrescription || isItemInactive}
                              onClick={() => {
                                printEvidenceOfPrescription(prescription)
                              }}
                            >
                              Print EoP
                            </MenuItem>
                          </Tooltip>
                          {!isReadonlyPermission && (
                            <Tooltip
                              label={
                                'Clicking this button disables all remaining repeats. This button is applicable to electronic prescriptions only. The script can be ceased even after the original script has been dispensed.'
                              }
                              placement={'right'}
                            >
                              <MenuItem
                                isDisabled={
                                  isPaperPrescription ||
                                  isItemInactive ||
                                  isDeactivatedPrescriber ||
                                  isReadonlyPermission ||
                                  !isSelfPrescribed
                                }
                                onClick={() => openCeaseModal(index)}
                              >
                                Cease
                              </MenuItem>
                            </Tooltip>
                          )}
                          {!isReadonlyPermission && (
                            <Tooltip
                              label={
                                'Clicking this button cancels the entire prescription. The script can only be cancelled provided the original script has never been dispensed.'
                              }
                              placement={'right'}
                            >
                              <MenuItem
                                isDisabled={
                                  isItemInactive ||
                                  isDeactivatedPrescriber ||
                                  isReadonlyPermission ||
                                  !isSelfPrescribed
                                }
                                onClick={() => openCancelModal(index)}
                              >
                                Cancel
                              </MenuItem>
                            </Tooltip>
                          )}
                          <Tooltip
                            label={
                              'Enable the same electronic prescription to be sent to the nominated mobile number and/or email address of the patient. SCID of a re-sent prescription will be the same as the original prescription.'
                            }
                            placement={'right'}
                          >
                            <MenuItem
                              isDisabled={
                                isPaperPrescription ||
                                isItemInactive ||
                                isEmergencySupply ||
                                isSendToPharmacy ||
                                isDeactivatedPrescriber ||
                                !isSelfPrescribed
                              }
                              onClick={() => openReIssueModal(index)}
                            >
                              Re-send
                            </MenuItem>
                          </Tooltip>
                        </MenuList>
                      </Menu>
                    </Td>
                    <Td>{patientName}</Td>
                    {isSharedPatientsFeatureOn && <Td>{prescriberName}</Td>}
                    {isMultipleLocationsFeatureOn && (
                      <Td>
                        <Flex align="center" gap={2} minW={0}>
                          <Icon
                            as={MdLocationOn}
                            color="red.400"
                            boxSize={4}
                            flexShrink={0}
                            aria-hidden
                          />
                          <Box flex="1" minW={0} color="gray.800">
                            <TruncatedText text={transformed.locationName || '—'} />
                          </Box>
                        </Flex>
                      </Td>
                    )}
                    <Td>{prescription.scid}</Td>
                    <Td>
                      <PrescriptionTypeTag isPaperPrescription={isPaperPrescription} />
                    </Td>
                    <Td>{createdDate}</Td>
                    <Td whiteSpace={'normal'}>
                      {prescribedItems.map((item, idx) => (
                        <Text key={`displayName_${idx}`}>
                          {item.displayName} <PrescribedItemStateTag state={item.state} />
                        </Text>
                      ))}
                    </Td>
                    <Td>
                      {prescribedItems.map((item, idx) => (
                        <Text key={`numberOfRepeatsAuthorised_${idx}`}>
                          {item.numberOfRepeatsAuthorised}
                        </Text>
                      ))}
                    </Td>
                    <Td whiteSpace={'normal'}>
                      {prescribedItems.map((item, idx) => (
                        <TruncatedText
                          key={`patientInstructions_${idx}`}
                          text={item.patientInstructions}
                        />
                      ))}
                    </Td>
                  </Tr>
                )
              })}
            </Tbody>
          </StyledTable>
        ) : (
          <Center gap={2}>
            <VStack justifyContent="center" alignItems="center" spacing={4} p={8}>
              <Box w={24} color="blue.500" p={4} borderRadius="2xl" backgroundColor="blue.50">
                <FiFileText />
              </Box>
              <Text color="dark.500" fontSize="lg" fontWeight="bold">
                No prescriptions found
              </Text>
            </VStack>
          </Center>
        )}
      </Box>
    </StyledWrapper>
  )
}

export default PrescriptionTable
